import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Zap, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";

const L = {
  en: { recommends: "recommends upgrading to", off: "off", review: "Review offer", dismiss: "Dismiss", failed: "Failed to dismiss" },
  es: { recommends: "recomienda mejorar a", off: "de descuento", review: "Ver oferta", dismiss: "Descartar", failed: "No se pudo descartar" },
  de: { recommends: "empfiehlt Upgrade auf", off: "Rabatt", review: "Angebot ansehen", dismiss: "Schliessen", failed: "Fehler beim Schliessen" },
};

/**
 * Renders a sticky cyan→violet banner when the client's attributed agency
 * has sent them a close-deal upgrade prompt. Dismissible + auto-hides once
 * the user clicks through to /pricing.
 *
 * Mount once at the top of <Layout/>.
 */
export default function CloseDealBanner() {
  const [prompts, setPrompts] = useState([]);
  const { language } = useLanguage();
  const t = L[language] || L.en;

  const load = async () => {
    try {
      const r = await api.get("/agency/my-prompts");
      setPrompts(r.data?.prompts || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3 * 60 * 1000); // refresh every 3 min
    return () => clearInterval(t);
  }, []);

  const dismiss = async (id) => {
    try {
      await api.post(`/agency/my-prompts/${id}/dismiss`);
      setPrompts(p => p.filter(x => x.prompt_id !== id));
    } catch { toast.error(t.failed); }
  };

  if (!prompts.length) return null;
  // Show the newest one only
  const p = prompts[0];

  return (
    <div
      className="sticky top-0 z-40 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-cyan-600 text-white"
      data-testid="close-deal-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Zap className="w-4 h-4 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-[13px] font-bold truncate" data-testid="close-deal-banner-title">
              {p.agency_name} {t.recommends} <span className="underline">{String(p.target_plan || "Pro").replace(/^\w/, c => c.toUpperCase())}</span>
              {p.discount_pct ? <span className="ml-1 bg-white/20 rounded px-1.5 py-0.5 text-[11px]">{p.discount_pct}% {t.off}</span> : null}
            </div>
            {p.note ? <div className="text-[11px] text-white/90 truncate italic">&ldquo;{p.note}&rdquo;</div> : null}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to={`/pricing?plan=${p.target_plan}&close_deal=${p.prompt_id}`}
            className="px-3 py-1 text-[12px] font-bold rounded-full bg-white text-violet-700 hover:bg-white/90 flex items-center gap-1"
            data-testid="close-deal-banner-cta"
          >
            {t.review} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <button
            onClick={() => dismiss(p.prompt_id)}
            className="p-1 rounded hover:bg-white/10"
            data-testid="close-deal-banner-dismiss"
            title={t.dismiss}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
